import { useCallback, useState, type ReactNode } from 'react';

import type { Form } from '../fixtures/domain.js';
import { FormPickerDialog } from '../form/FormPickerDialog.js';
import { JobPickerDialog } from './JobPickerDialog.js';

/** The two sends the pickers drive — the slice of a conversation they need. */
export interface PickerConvo {
  sendForm: (form: Form, note: string) => Promise<void>;
  sendJob: (jobId: string) => Promise<void>;
}

interface Options {
  /** Passed in, not fetched here, so a surface that already reads it (e.g. /chat) does not fetch twice. */
  formLibrary: { forms: Form[]; error: boolean };
  /** Out to the Forms section; omitted where there is none (the widget), which hides the link. */
  onManageForms?: () => void;
  /** A failed send surfaces here; omitted means the optimistic close stands. */
  onError?: (message: string) => void;
  /** Scope the picker modals to a floating panel (the widget) instead of the viewport. */
  contained?: boolean;
}

interface ComposerPickers {
  /** Attach to the composer; a caller on a broadcast channel passes undefined instead. */
  onForm: () => void;
  onPickJob: () => void;
  /** Render once near the composer; the dialogs manage their own open state. */
  dialogs: ReactNode;
  /** Close both — for a surface that resets its panes on navigation. */
  reset: () => void;
}

/**
 * The composer's `+ › Form` and `+ › Job details` actions, their dialogs, and their
 * sends, in one place so `/chat` and the widget stay in step: a new picker added
 * here appears on both surfaces at once, which is the drift this replaces.
 */
export function useComposerPickers(convo: PickerConvo, options: Options): ComposerPickers {
  const { formLibrary, onManageForms, onError, contained = false } = options;
  const [formOpen, setFormOpen] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);

  const dialogs = (
    <>
      {formOpen && (
        <FormPickerDialog
          forms={formLibrary.forms}
          sending={false}
          error={formLibrary.error}
          onClose={() => setFormOpen(false)}
          onSend={(form, note) => {
            // Optimistic, like the composer: the dialog closes before the send resolves.
            setFormOpen(false);
            void convo.sendForm(form, note).catch(() => onError?.('Could not send the form.'));
          }}
          contained={contained}
          {...(onManageForms ? { onManage: onManageForms } : {})}
        />
      )}
      {jobOpen && (
        <JobPickerDialog
          onClose={() => setJobOpen(false)}
          onSendJob={convo.sendJob}
          contained={contained}
        />
      )}
    </>
  );

  const onForm = useCallback(() => setFormOpen(true), []);
  const onPickJob = useCallback(() => setJobOpen(true), []);
  const reset = useCallback(() => {
    setFormOpen(false);
    setJobOpen(false);
  }, []);

  return { onForm, onPickJob, dialogs, reset };
}
